
import React, { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';

interface SystemVariable {
  id: string;
  name: string;
  value: string;
  description: string | null;
  is_protected: boolean;
}

export function SystemVariables() {
  const [variables, setVariables] = useState<SystemVariable[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingVariable, setEditingVariable] = useState<SystemVariable | null>(null);
  const [variableName, setVariableName] = useState('');
  const [variableValue, setVariableValue] = useState('');
  const [variableDescription, setVariableDescription] = useState('');
  const [isProtected, setIsProtected] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchSystemVariables();
  }, []);

  const fetchSystemVariables = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('system_variables')
        .select('*')
        .order('name');
      
      if (error) throw error;
      setVariables(data || []);
    } catch (error: any) {
      console.error('Error fetching system variables:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to load system variables',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (variable: SystemVariable) => {
    setEditingVariable(variable);
    setVariableName(variable.name);
    setVariableValue(variable.value);
    setVariableDescription(variable.description || '');
    setIsProtected(variable.is_protected);
    setShowDialog(true);
  };

  const handleNewVariable = () => {
    setEditingVariable(null);
    setVariableName('');
    setVariableValue('');
    setVariableDescription('');
    setIsProtected(false);
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (!variableName || !variableValue) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Name and value are required',
        });
        return;
      }

      if (editingVariable) {
        // Update existing variable
        const { error } = await supabase
          .from('system_variables')
          .update({
            name: variableName,
            value: variableValue,
            description: variableDescription || null,
            is_protected: isProtected,
          })
          .eq('id', editingVariable.id);

        if (error) throw error;
        toast({
          title: 'Variable Updated',
          description: `Variable "${variableName}" has been updated`,
        });
      } else {
        // Create new variable
        const { error } = await supabase
          .from('system_variables')
          .insert({
            name: variableName,
            value: variableValue,
            description: variableDescription || null,
            is_protected: isProtected,
          });

        if (error) throw error;
        toast({
          title: 'Variable Created',
          description: `Variable "${variableName}" has been created`,
        });
      }
      
      setShowDialog(false);
      fetchSystemVariables();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to save variable',
      });
    }
  };

  const handleDelete = async (id: string, name: string) => {
    try {
      const { error } = await supabase
        .from('system_variables')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setVariables(variables.filter(v => v.id !== id));
      toast({
        title: 'Variable Deleted',
        description: `Variable "${name}" has been deleted`,
      });
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to delete variable',
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">System Variables</h3>
        <Button onClick={handleNewVariable} size="sm">
          <Plus className="h-4 w-4 mr-1" />
          New Variable
        </Button>
      </div>

      <div className="border rounded-md">
        {loading ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center space-x-4">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-64 flex-1" />
                <Skeleton className="h-8 w-16" />
              </div>
            ))}
          </div>
        ) : variables.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            No system variables found. Add one to get started.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Value</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {variables.map((variable) => (
                <TableRow key={variable.id}>
                  <TableCell className="font-medium">
                    <code>{variable.name}</code>
                  </TableCell>
                  <TableCell className="max-w-xs truncate">{variable.value}</TableCell>
                  <TableCell className="max-w-xs truncate text-muted-foreground">
                    {variable.description || '-'}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleEdit(variable)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {!variable.is_protected && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleDelete(variable.id, variable.name)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingVariable ? 'Edit Variable' : 'Add New Variable'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <label className="text-right text-sm">Name:</label>
              <Input 
                className="col-span-3" 
                value={variableName} 
                onChange={(e) => setVariableName(e.target.value)} 
                placeholder="COMPANY_NAME"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <label className="text-right text-sm">Value:</label>
              <Input 
                className="col-span-3" 
                value={variableValue} 
                onChange={(e) => setVariableValue(e.target.value)} 
                placeholder="DriveCourses"
              />
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <label className="text-right text-sm">Description:</label>
              <Textarea 
                className="col-span-3 min-h-[80px]" 
                value={variableDescription}
                onChange={(e) => setVariableDescription(e.target.value)}
                placeholder="Company name used in email templates"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <label className="text-right text-sm">Protected:</label>
              <div className="col-span-3">
                <input
                  type="checkbox"
                  checked={isProtected}
                  onChange={(e) => setIsProtected(e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm text-muted-foreground">
                  Protected variables cannot be deleted
                </span>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
